import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../../config';

const KnowledgeBase = () => {
  // State for modal and form
  const [showModal, setShowModal] = useState(false);
  const [kbName, setKbName] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // State for listing KBs
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loadingKBs, setLoadingKBs] = useState(false);

  // State for agent attachment
  const [attachModal, setAttachModal] = useState(false);
  const [selectedKB, setSelectedKB] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [attachStatus, setAttachStatus] = useState('');
  
  // State for user's agents
  const [userAgents, setUserAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  // State for agent-to-KB mapping
  const [agentKBMap, setAgentKBMap] = useState({});
  const [loadingAgentKBMap, setLoadingAgentKBMap] = useState(false);

  // State for KB management
  const [viewModal, setViewModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteKBModal, setDeleteKBModal] = useState(false);
  const [detachModal, setDetachModal] = useState(false);
  const [selectedKBDetails, setSelectedKBDetails] = useState(null);
  const [loadingKBDetails, setLoadingKBDetails] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  const [deletingKB, setDeletingKB] = useState(false);
  const [detachingKB, setDetachingKB] = useState(false);
  const [selectedAgentToDetach, setSelectedAgentToDetach] = useState('');

  // State for file management
  const [addFilesModal, setAddFilesModal] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const [addingFiles, setAddingFiles] = useState(false);
  const [deleteFileModal, setDeleteFileModal] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);
  const [selectedFileToDelete, setSelectedFileToDelete] = useState(null);

  // User info
  const [userId, setUserId] = useState('');
  const [userError, setUserError] = useState('');

  // Admin KB state
  const [adminKBActive, setAdminKBActive] = useState(false);
  const [adminKBModal, setAdminKBModal] = useState(false);
  const [selectedAgentForKB, setSelectedAgentForKB] = useState('');
  const [adminKBForm, setAdminKBForm] = useState({
    topic: '',
    content: '',
    tags: '',
    source_url: ''
  });
  const [adminKBSaving, setAdminKBSaving] = useState(false);
  const [adminKBError, setAdminKBError] = useState('');
  const [adminKBSuccess, setAdminKBSuccess] = useState('');
  
  // Admin KB file upload state
  const [adminKBFileUpload, setAdminKBFileUpload] = useState(false);
  const [adminKBUploadFiles, setAdminKBUploadFiles] = useState([]);
  const [adminKBUploading, setAdminKBUploading] = useState(false);
  
  // Admin KB entries for main display
  const [allAdminKBEntries, setAllAdminKBEntries] = useState([]);
  const [loadingAllAdminKB, setLoadingAllAdminKB] = useState(false);

  // Admin KB entry management state
  const [editAdminKBModal, setEditAdminKBModal] = useState(false);
  const [deleteAdminKBModal, setDeleteAdminKBModal] = useState(false);
  const [selectedAdminKBEntry, setSelectedAdminKBEntry] = useState(null);
  const [editingAdminKBForm, setEditingAdminKBForm] = useState({
    topic: '',
    content: '',
    tags: '',
    source_url: '',
    agent_id: ''
  });
  const [updatingAdminKB, setUpdatingAdminKB] = useState(false);
  const [deletingAdminKB, setDeletingAdminKB] = useState(false);

  let userEmail = '';
  try {
    const chatbotUser = JSON.parse(localStorage.getItem('chatbotUser'));
    userEmail = chatbotUser?.email || '';
  } catch (e) {
    console.error('Failed to parse chatbotUser from localStorage:', e);
  }
  
  // Fetch user_id from backend using email
  useEffect(() => {
    if (!userEmail) {
      setUserError('User email not found in localStorage.');
      return;
    }
    fetch(`${API_BASE_URL}/api/users/by-email?email=${encodeURIComponent(userEmail)}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.id) {
          setUserId(data.id);
          setUserError('');
        } else {
          setUserError('User not found.');
        }
      })
      .catch(() => setUserError('Failed to fetch user info.'));
  }, [userEmail]);

  // Fetch user's agents
  useEffect(() => {
    if (!userEmail) return;
    setLoadingAgents(true);
    fetch(`${API_BASE_URL}/api/user-agents?user_email=${encodeURIComponent(userEmail)}`)
      .then(res => res.json())
      .then(data => {
        setUserAgents(Array.isArray(data) ? data : []);
      })
      .catch(() => setUserAgents([]))
      .finally(() => setLoadingAgents(false));
  }, [userEmail]);

  // Fetch user's KBs
  useEffect(() => {
    if (!userId) return;
    setLoadingKBs(true);
    fetch(`${API_BASE_URL}/api/knowledge-bases?user_id=${userId}`)
      .then(res => res.json())
      .then(data => setKnowledgeBases(Array.isArray(data) ? data : []))
      .catch(() => setKnowledgeBases([]))
      .finally(() => setLoadingKBs(false));
    
    // Also load Admin KB entries
    loadAllAdminKBEntries();
  }, [userId, success]);

  // When opening the attach modal, fetch the agent-to-KB mapping
  useEffect(() => {
    if (!attachModal || !selectedKB || !userId) {
      setAgentKBMap({});
      return;
    }
    setLoadingAgentKBMap(true);
    fetch(`${API_BASE_URL}/api/user-agent-kb-map?user_id=${userId}`)
      .then(res => res.json())
      .then(data => {
        setAgentKBMap(data || {});
        setLoadingAgentKBMap(false);
        console.log('[KnowledgeBase.jsx] agentKBMap:', data);
      })
      .catch(err => {
        setAgentKBMap({});
        setLoadingAgentKBMap(false);
        console.error('[KnowledgeBase.jsx] Error fetching agentKBMap:', err);
      });
  }, [attachModal, selectedKB, userId]);

  // Handle file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      
      // Check file sizes
      const validFiles = [];
      const errors = [];
      
      newFiles.forEach(file => {
        if (file.size > maxFileSize) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          errors.push(`${file.name}: File too large (${fileSizeMB}MB). Maximum file size is 10MB. Please upload a smaller file.`);
        } else {
          validFiles.push(file);
        }
      });
      
      if (errors.length > 0) {
        setError(errors.join('\n'));
      } else {
        setFiles([...files, ...validFiles]);
        setError('');
      }
      
      // Reset the file input to allow selecting the same files again if needed
      e.target.value = '';
    }
  };

  // Remove a file
  const removeFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  // Handle KB creation
  const handleCreateKB = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');
    setSuccess('');
    try {
      if (!kbName || files.length === 0) {
        setError('Please provide a name and at least one file.');
        setUploading(false);
        return;
      }
      if (!userId) {
        setError('User ID not loaded.');
        setUploading(false);
        return;
      }
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('kbName', kbName);
      files.forEach(f => formData.append('files', f));
      const res = await fetch(`${API_BASE_URL}/api/knowledge-bases`, {
        method: 'POST',
        body: formData,
      });

      // Check if response is JSON before parsing
      const contentType = res.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      } else {
        // If not JSON, read as text to see what we got
        const text = await res.text();
        console.error('❌ Non-JSON response received:', text);
        
        // Check for common error patterns
        if (text.includes('File too large') || text.includes('LIMIT_FILE_SIZE')) {
          throw new Error('File too large. Maximum file size is 10MB. Please upload a smaller file.');
        } else if (text.includes('<!DOCTYPE') || text.includes('<html')) {
          throw new Error('Server error: The file may be too large or the server encountered an error. Please try uploading a smaller file.');
        } else {
          throw new Error(`Upload failed: ${text.substring(0, 200)}`);
        }
      }
      
      if (!res.ok) {
        // Handle specific error codes
        if (data.code === 'FILE_TOO_LARGE') {
          throw new Error(data.error || 'File too large. Maximum file size is 10MB. Please upload a smaller file.');
        } else if (data.code === 'TOO_MANY_FILES') {
          throw new Error(data.error || 'Too many files. Please upload fewer files at once.');
        } else {
          throw new Error(data.error || 'Upload failed');
        }
      }
      setSuccess('Knowledge base created!');
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setShowModal(false);
        setKbName('');
        setFiles([]);
      }, 2000); // Close after 2 seconds
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle attach KB to agent
  const handleAttachKB = async (e) => {
    e.preventDefault();
    setAttachStatus('');
    if (!selectedKB || !selectedAgentId) {
      setAttachStatus('Please select both a knowledge base and an agent.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents/${selectedAgentId}/attach-kb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ knowledge_base_id: selectedKB.id, user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Attach failed');
      setAttachStatus(`Knowledge base "${selectedKB.name}" successfully attached to agent!`);
      setAttachModal(false);
      setSelectedAgentId('');
      setSelectedKB(null);
    } catch (err) {
      setAttachStatus(err.message);
    }
  };

  // Handle view KB details
  const handleViewKB = async (kb) => {
    setSelectedKB(kb);
    setLoadingKBDetails(true);
    setViewModal(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/knowledge-bases/${kb.id}?user_id=${userId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch KB details');
      setSelectedKBDetails(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingKBDetails(false);
    }
  };

  // Handle edit KB name
  const handleEditKB = (kb) => {
    setSelectedKB(kb);
    setEditingName(kb.name);
    setEditModal(true);
  };

  // Handle update KB name
  const handleUpdateKBName = async (e) => {
    e.preventDefault();
    if (!editingName.trim()) {
      setError('Knowledge base name cannot be empty.');
      return;
    }
    setUpdatingName(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/knowledge-bases/${selectedKB.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingName.trim(), user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setSuccess('Knowledge base name updated successfully!');
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setEditModal(false);
        setSelectedKB(null);
        setEditingName('');
      }, 2000); // Close after 2 seconds
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingName(false);
    }
  };

  // Handle delete KB
  const handleDeleteKB = (kb) => {
    setSelectedKB(kb);
    setDeleteKBModal(true);
  };

  // Handle confirm delete KB
  const handleConfirmDeleteKB = async () => {
    setDeletingKB(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/knowledge-bases/${selectedKB.id}?user_id=${userId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      setSuccess(`Knowledge base "${selectedKB.name}" deleted successfully!`);
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setDeleteKBModal(false);
        setSelectedKB(null);
      }, 2000); // Close after 2 seconds
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingKB(false);
    }
  };

  // Handle detach KB from agent
  const handleDetachKB = (kb) => {
    setSelectedKB(kb);
    setDetachModal(true);
  };

  // Handle confirm detach KB from agent
  const handleConfirmDetachKB = async () => {
    if (!selectedAgentToDetach) {
      setError('Please select an agent to detach from.');
      return;
    }
    setDetachingKB(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/knowledge-bases/${selectedKB.id}/detach-from-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_id: selectedAgentToDetach, user_id: userId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Detach failed');
      setSuccess(`Knowledge base "${selectedKB.name}" detached from agent successfully!`);
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setDetachModal(false);
        setSelectedKB(null);
        setSelectedAgentToDetach('');
      }, 2000); // Close after 2 seconds
    } catch (err) {
      setError(err.message);
    } finally {
      setDetachingKB(false);
    }
  };

  // Handle add files to KB
  const handleAddFiles = (kb) => {
    setSelectedKB(kb);
    setNewFiles([]);
    setAddFilesModal(true);
  };

  // Handle file selection for adding
  const handleFileSelection = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      
      console.log('[frontend] Selected files:', selectedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));
      
      // Check file sizes
      const validFiles = [];
      const errors = [];
      
      selectedFiles.forEach(file => {
        if (file.size > maxFileSize) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          errors.push(`${file.name}: File too large (${fileSizeMB}MB). Maximum file size is 10MB. Please upload a smaller file.`);
        } else {
          validFiles.push(file);
        }
      });
      
      if (errors.length > 0) {
        setError(errors.join('\n'));
      } else {
        setNewFiles([...newFiles, ...validFiles]);
        setError('');
      }
      
      // Reset the file input to allow selecting the same files again if needed
      e.target.value = '';
    }
  };

  // Remove file from selection
  const removeNewFile = (idx) => {
    setNewFiles(newFiles.filter((_, i) => i !== idx));
  };

  // Handle confirm add files
  const handleConfirmAddFiles = async (e) => {
    e.preventDefault();
    if (newFiles.length === 0) {
      setError('Please select at least one file.');
      return;
    }
    setAddingFiles(true);
    setError('');
    try {
      console.log('[frontend] Submitting files:', newFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));
      const formData = new FormData();
      formData.append('user_id', userId);
      newFiles.forEach(f => {
        console.log('[frontend] Appending file to FormData:', f.name, f.size);
        formData.append('files', f);
      });
      
      const res = await fetch(`${API_BASE_URL}/api/knowledge-bases/${selectedKB.id}/add-files`, {
        method: 'POST',
        body: formData,
      });

      // Check if response is JSON before parsing
      const contentType = res.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      } else {
        // If not JSON, read as text to see what we got
        const text = await res.text();
        console.error('❌ Non-JSON response received:', text);
        
        // Check for common error patterns
        if (text.includes('File too large') || text.includes('LIMIT_FILE_SIZE')) {
          throw new Error('File too large. Maximum file size is 10MB. Please upload a smaller file.');
        } else if (text.includes('<!DOCTYPE') || text.includes('<html')) {
          throw new Error('Server error: The file may be too large or the server encountered an error. Please try uploading a smaller file.');
        } else {
          throw new Error(`Upload failed: ${text.substring(0, 200)}`);
        }
      }
      
      if (!res.ok) {
        // Handle specific error codes
        if (data.code === 'FILE_TOO_LARGE') {
          throw new Error(data.error || 'File too large. Maximum file size is 10MB. Please upload a smaller file.');
        } else if (data.code === 'TOO_MANY_FILES') {
          throw new Error(data.error || 'Too many files. Please upload fewer files at once.');
        } else {
          throw new Error(data.error || 'Add files failed');
        }
      }
      setSuccess(`Successfully added ${data.total_files} files to knowledge base!`);
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setAddFilesModal(false);
        setSelectedKB(null);
        setNewFiles([]);
      }, 2000); // Close after 2 seconds
    } catch (err) {
      setError(err.message);
    } finally {
      setAddingFiles(false);
    }
  };

  // Handle delete file
  const handleDeleteFile = (file) => {
    setSelectedFileToDelete(file);
    setDeleteFileModal(true);
  };

  // Handle confirm delete file
  const handleConfirmDeleteFile = async () => {
    if (!selectedFileToDelete) return;
    setDeletingFile(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/knowledge-bases/${selectedKB.id}/files/${selectedFileToDelete.id}?user_id=${userId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete file failed');
      setSuccess(`File "${selectedFileToDelete.filename}" deleted successfully!`);
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setDeleteFileModal(false);
        setSelectedFileToDelete(null);
      }, 2000); // Close after 2 seconds
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingFile(false);
    }
  };

  // Handle download file
  const handleDownloadFile = async (file) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/knowledge-bases/${selectedKB.id}/files/${file.id}/download?user_id=${userId}`);
      if (!res.ok) throw new Error('Download failed');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download file: ' + err.message);
    }
  };

  // Admin KB functions
  const parsedTags = useMemo(
    () => adminKBForm.tags.split(",").map(t => t.trim()).filter(Boolean),
    [adminKBForm.tags]
  );


  // Load all Admin KB entries for main display
  const loadAllAdminKBEntries = async () => {
    if (!userId) return;
    
    setLoadingAllAdminKB(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-kb/user/${userId}`);
      const data = await response.json();
      
      if (response.ok) {
        setAllAdminKBEntries(data.entries || []);
      } else {
        console.error('Failed to load Admin KB entries:', data.error);
        setAllAdminKBEntries([]);
      }
    } catch (err) {
      console.error('Failed to load all Admin KB entries:', err);
      setAllAdminKBEntries([]);
    } finally {
      setLoadingAllAdminKB(false);
    }
  };

  // Handle admin KB form submission
  const handleAdminKBSave = async (e) => {
    e.preventDefault();
    if (!selectedAgentForKB || !userId) {
      setAdminKBError('Please select an agent first');
      return;
    }

    // Validation
    if (!adminKBForm.topic.trim() || adminKBForm.topic.trim().length < 3 || adminKBForm.topic.trim().length > 120) {
      setAdminKBError('Topic must be between 3-120 characters');
      return;
    }
    if (!adminKBForm.content.trim()) {
      setAdminKBError('Content cannot be empty');
      return;
    }
    if (parsedTags.length > 10) {
      setAdminKBError('Maximum 10 tags allowed');
      return;
    }
    if (parsedTags.some(tag => tag.length > 32)) {
      setAdminKBError('Each tag must be 32 characters or less');
      return;
    }
    if (adminKBForm.source_url && !adminKBForm.source_url.startsWith('https://')) {
      setAdminKBError('Source URL must start with https://');
      return;
    }

    setAdminKBSaving(true);
    setAdminKBError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-kb/upsert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: selectedAgentForKB,
          user_id: userId,
          topic: adminKBForm.topic.trim(),
          content: adminKBForm.content.trim(),
          tags: parsedTags.length ? parsedTags.map(tag => tag.toLowerCase()) : undefined,
          source_url: adminKBForm.source_url.trim() || undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save KB entry');
      }
      
      setAdminKBSuccess('KB entry saved successfully!');
      setAdminKBForm({ topic: '', content: '', tags: '', source_url: '' });
      // Refresh the main display
      await loadAllAdminKBEntries();
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setAdminKBModal(false);
        setSelectedAgentForKB('');
        setAdminKBForm({ topic: '', content: '', tags: '', source_url: '' });
      }, 2000); // Close after 2 seconds
    } catch (err) {
      setAdminKBError(err.message || 'Failed to save KB entry');
    } finally {
      setAdminKBSaving(false);
    }
  };


  // Handle Admin KB file selection
  const handleAdminKBFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const maxFileSize = 10 * 1024 * 1024; // 10MB
      
      // Filter for allowed file types and check file sizes
      const allowedTypes = ['.csv', '.txt', '.pdf', '.doc', '.docx', '.xlsx', '.xls'];
      const validFiles = [];
      const errors = [];
      
      newFiles.forEach(file => {
        const extension = '.' + file.name.split('.').pop().toLowerCase();
        
        // Check file type
        if (!allowedTypes.includes(extension)) {
          errors.push(`${file.name}: File type not allowed. Only CSV, TXT, PDF, DOC, DOCX, XLSX, and XLS files are allowed.`);
          return;
        }
        
        // Check file size
        if (file.size > maxFileSize) {
          const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
          errors.push(`${file.name}: File too large (${fileSizeMB}MB). Maximum file size is 10MB. Please upload a smaller file.`);
          return;
        }
        
        validFiles.push(file);
      });
      
      if (errors.length > 0) {
        setAdminKBError(errors.join('\n'));
      } else {
        setAdminKBUploadFiles([...adminKBUploadFiles, ...validFiles]);
        setAdminKBError('');
      }
      
      // Reset the file input to allow selecting the same files again if needed
      e.target.value = '';
    }
  };

  // Remove Admin KB file
  const removeAdminKBFile = (idx) => {
    setAdminKBUploadFiles(adminKBUploadFiles.filter((_, i) => i !== idx));
  };

  // Handle Admin KB file upload
  const handleAdminKBFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedAgentForKB || !userId) {
      setAdminKBError('Please select an agent first');
      return;
    }
    if (adminKBUploadFiles.length === 0) {
      setAdminKBError('Please select at least one file');
      return;
    }

    setAdminKBUploading(true);
    setAdminKBError('');
    
    try {
      const formData = new FormData();
      formData.append('agent_id', selectedAgentForKB);
      formData.append('user_id', userId);
      adminKBUploadFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_BASE_URL}/api/admin-kb/upload`, {
        method: 'POST',
        body: formData,
      });

      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // If not JSON, read as text to see what we got
        const text = await response.text();
        console.error('❌ Non-JSON response received:', text);
        
        // Check for common error patterns
        if (text.includes('File too large') || text.includes('LIMIT_FILE_SIZE')) {
          throw new Error('File too large. Maximum file size is 10MB. Please upload a smaller file.');
        } else if (text.includes('<!DOCTYPE') || text.includes('<html')) {
          throw new Error('Server error: The file may be too large or the server encountered an error. Please try uploading a smaller file.');
        } else {
          throw new Error(`Upload failed: ${text.substring(0, 200)}`);
        }
      }
      
      if (!response.ok) {
        // Handle specific error codes
        if (data.code === 'FILE_TOO_LARGE') {
          throw new Error(data.error || 'File too large. Maximum file size is 10MB. Please upload a smaller file.');
        } else if (data.code === 'TOO_MANY_FILES') {
          throw new Error(data.error || 'Too many files. Please upload fewer files at once.');
        } else {
          throw new Error(data.error || 'File upload failed');
        }
      }

      // Show detailed success message with chunking info
      const successMessage = data.results && data.results.length > 0 
        ? `Successfully uploaded and processed ${data.results.length} file(s)! Content has been automatically chunked for better search performance.`
        : `Successfully uploaded ${adminKBUploadFiles.length} file(s)!`;
      
      setAdminKBSuccess(successMessage);
      setAdminKBUploadFiles([]);
      setAdminKBFileUpload(false);
      // Refresh the main display
      await loadAllAdminKBEntries();
      
      // Auto-close modal after showing success message
      setTimeout(() => {
        setAdminKBModal(false);
        setSelectedAgentForKB('');
        setAdminKBUploadFiles([]);
        setAdminKBFileUpload(false);
      }, 2000); // Close after 2 seconds
    } catch (err) {
      setAdminKBError(err.message || 'Failed to upload files');
    } finally {
      setAdminKBUploading(false);
    }
  };



  // Handle edit Admin KB entry
  const handleEditAdminKBEntry = (entry) => {
    setSelectedAdminKBEntry(entry);
    setEditingAdminKBForm({
      topic: entry.topic,
      content: entry.content,
      tags: entry.tags ? entry.tags.join(', ') : '',
      source_url: entry.source_url || '',
      agent_id: entry.agent_id || ''
    });
    setEditAdminKBModal(true);
  };

  // Handle update Admin KB entry
  const handleUpdateAdminKBEntry = async (e) => {
    e.preventDefault();
    if (!selectedAdminKBEntry || !userId) {
      setAdminKBError('Missing required data');
      return;
    }

    // Validation
    if (!editingAdminKBForm.topic.trim() || editingAdminKBForm.topic.trim().length < 3 || editingAdminKBForm.topic.trim().length > 120) {
      setAdminKBError('Topic must be between 3-120 characters');
      return;
    }
    // Content validation removed since content field is hidden in edit modal
    if (!editingAdminKBForm.agent_id) {
      setAdminKBError('Please select an agent');
      return;
    }

    const parsedTags = editingAdminKBForm.tags.split(",").map(t => t.trim()).filter(Boolean);
    if (parsedTags.length > 10) {
      setAdminKBError('Maximum 10 tags allowed');
      return;
    }
    if (parsedTags.some(tag => tag.length > 32)) {
      setAdminKBError('Each tag must be 32 characters or less');
      return;
    }
    if (editingAdminKBForm.source_url && editingAdminKBForm.source_url.trim()) {
      const url = editingAdminKBForm.source_url.trim();
      // Allow various URL formats including S3, AWS, and other valid formats
      const isValidUrl = url.match(/^(https?:\/\/|ftp:\/\/|file:\/\/|s3:\/\/|s3a:\/\/|s3n:\/\/|\/\/|data:|mailto:|tel:|#)/) || 
                        url.includes('.') || // Allow URLs with dots (like domain names)
                        url.startsWith('/') || // Allow relative paths
                        url.startsWith('./') || // Allow relative paths
                        url.startsWith('../'); // Allow relative paths
      
      if (!isValidUrl) {
        setAdminKBError('Please enter a valid URL or leave empty');
        return;
      }
    }

    setUpdatingAdminKB(true);
    setAdminKBError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-kb/upsert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: editingAdminKBForm.agent_id,
          user_id: userId,
          topic: editingAdminKBForm.topic.trim(),
          content: editingAdminKBForm.content.trim(),
          tags: parsedTags.length ? parsedTags.map(tag => tag.toLowerCase()) : undefined,
          source_url: editingAdminKBForm.source_url.trim() || undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update KB entry');
      }
      
      setAdminKBSuccess('KB entry updated successfully!');
      setEditAdminKBModal(false);
      setSelectedAdminKBEntry(null);
      setEditingAdminKBForm({ topic: '', content: '', tags: '', source_url: '', agent_id: '' });
      // Refresh the main display
      await loadAllAdminKBEntries();
    } catch (err) {
      setAdminKBError(err.message || 'Failed to update KB entry');
    } finally {
      setUpdatingAdminKB(false);
    }
  };

  // Handle delete Admin KB entry
  const handleDeleteAdminKBEntry = (entry) => {
    setSelectedAdminKBEntry(entry);
    setDeleteAdminKBModal(true);
  };

  // Handle confirm delete Admin KB entry
  const handleConfirmDeleteAdminKBEntry = async () => {
    if (!selectedAdminKBEntry || !userId) {
      setAdminKBError('Missing required data');
      return;
    }

    setDeletingAdminKB(true);
    setAdminKBError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin-kb/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agent_id: selectedAdminKBEntry.agent_id,
          user_id: userId,
          topic: selectedAdminKBEntry.topic,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete KB entry');
      }
      
      setAdminKBSuccess('KB entry deleted successfully!');
      
      // Auto-close modal after showing success message
      setTimeout(async () => {
        setDeleteAdminKBModal(false);
        setSelectedAdminKBEntry(null);
        // Refresh the main display
        await loadAllAdminKBEntries();
      }, 2000); // Close after 2 seconds
    } catch (err) {
      setAdminKBError(err.message || 'Failed to delete KB entry');
    } finally {
      setDeletingAdminKB(false);
    }
  };

  // Clear admin KB success message after 3 seconds
  useEffect(() => {
    if (adminKBSuccess) {
      const timer = setTimeout(() => {
        setAdminKBSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [adminKBSuccess]);

  return (
    <div className="container d-flex flex-column h-100" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: 'calc(100vh - 80px)' }}>
      <div style={{ background: '#fff', borderRadius: '50px', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', padding: '20px', minWidth: '900px', maxWidth: '1100px', width: '100%', color: '#111', marginTop: '0', marginBottom: '48px', height: '500px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ fontWeight: 'bold', fontSize: '1.3rem', color: '#222', margin: 0, letterSpacing: '0.5px' }}>Knowledge Base</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button 
              style={{ 
                background: '#10b981', 
                color: '#fff', 
                border: 'none', 
                borderRadius: '50%', 
                width: 40, 
                height: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: 22, 
                cursor: 'pointer'
              }} 
              onClick={() => setAdminKBModal(true)}
              title="Add Admin KB Entry"
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>
        {/* Scrollable content area below header */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {userError && <div style={{ color: 'red', marginBottom: 8 }}>{userError}</div>}
          
          {/* Combined Knowledge Base Interface */}
          {loadingKBs || loadingAllAdminKB ? (
            <div>Loading knowledge bases...</div>
          ) : allAdminKBEntries.length === 0 ? (
            <div>No knowledge bases found.</div>
          ) : (
            <div>
              {/* Admin KB Entries */}
              {allAdminKBEntries.length > 0 && (
                <div>
                  <h3 style={{ marginBottom: 12, fontSize: '1.1rem', fontWeight: 600, color: '#374151' }}>Admin Knowledge Base Entries</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                    <thead>
                      <tr style={{ background: '#f3f4f6' }}>
                        <th style={{ padding: 8, textAlign: 'left' }}>Topic</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Agent</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Updated</th>
                        <th style={{ padding: 8, textAlign: 'left' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allAdminKBEntries.map(entry => (
                        <tr key={`${entry.agent_id}-${entry.id}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: 8, fontWeight: 500 }}>{entry.topic}</td>
                          <td style={{ padding: 8 }}>{entry.agent_name}</td>
                          <td style={{ padding: 8 }}>{new Date(entry.updated_at).toLocaleDateString()}</td>
                          <td style={{ padding: 8 }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button 
                                onClick={() => handleEditAdminKBEntry(entry)}
                                style={{ 
                                  background: '#10b981', 
                                  color: '#fff', 
                                  border: 'none', 
                                  borderRadius: 4, 
                                  padding: '4px 8px', 
                                  fontSize: 12, 
                                  cursor: 'pointer' 
                                }}
                                title="Edit Entry"
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => handleDeleteAdminKBEntry(entry)}
                                style={{ 
                                  background: '#ef4444', 
                                  color: '#fff', 
                                  border: 'none', 
                                  borderRadius: 4, 
                                  padding: '4px 8px', 
                                  fontSize: 12, 
                                  cursor: 'pointer' 
                                }}
                                title="Delete Entry"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
        {success && <div style={{ color: 'green', marginTop: 8 }}>{success}</div>}
        {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
        {attachStatus && <div style={{ color: attachStatus.includes('successfully') ? 'green' : 'red', marginTop: 8 }}>{attachStatus}</div>}
      </div>

      {/* Admin KB Modal */}
      {adminKBModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Admin Knowledge Base</h2>
              <button 
                onClick={() => setAdminKBModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            {/* Agent Selection */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Select Agent for KB Management</label>
              <select
                value={selectedAgentForKB}
                onChange={e => setSelectedAgentForKB(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
              >
                <option value="">Select an agent...</option>
                {userAgents.map(agent => (
                  <option key={agent.agent_id} value={agent.agent_id}>
                    {agent.agent_name || agent.agent_id}
                  </option>
                ))}
              </select>
            </div>

            {selectedAgentForKB && (
              <>
                {/* Admin KB Mode Selection */}
                <div style={{ marginBottom: 20, display: 'flex', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => setAdminKBFileUpload(false)}
                    style={{
                      background: !adminKBFileUpload ? '#10b981' : '#f3f4f6',
                      color: !adminKBFileUpload ? '#fff' : '#374151',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontSize: 14,
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Manual Entry
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdminKBFileUpload(true)}
                    style={{
                      background: adminKBFileUpload ? '#10b981' : '#f3f4f6',
                      color: adminKBFileUpload ? '#fff' : '#374151',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 16px',
                      fontSize: 14,
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Upload Documents
                  </button>
                </div>

                {!adminKBFileUpload ? (
                  /* Admin KB Form */
                <form onSubmit={handleAdminKBSave} style={{ marginBottom: 24, padding: 20, background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                  <h3 style={{ marginBottom: 16, fontSize: '1.1rem', fontWeight: 600 }}>Add/Update KB Entry</h3>
                  
                  <div style={{ display: 'grid', gap: 16 }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Topic *</label>
                      <input
                        type="text"
                        value={adminKBForm.topic}
                        onChange={e => setAdminKBForm({...adminKBForm, topic: e.target.value})}
                        placeholder="Enter topic (3-120 characters)"
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                        required
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Content *</label>
                      <textarea
                        value={adminKBForm.content}
                        onChange={e => setAdminKBForm({...adminKBForm, content: e.target.value})}
                        placeholder="Enter content (Markdown or plain text)"
                        rows={6}
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none', resize: 'vertical' }}
                        required
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Tags</label>
                      <input
                        type="text"
                        value={adminKBForm.tags}
                        onChange={e => setAdminKBForm({...adminKBForm, tags: e.target.value})}
                        placeholder="pricing, faq, support (comma-separated, max 10 tags)"
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Source URL (optional)</label>
                      <input
                        type="url"
                        value={adminKBForm.source_url}
                        onChange={e => setAdminKBForm({...adminKBForm, source_url: e.target.value})}
                        placeholder="https://example.com"
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                      />
                    </div>
                    
                    <div>
                      <button 
                        type="submit" 
                        disabled={adminKBSaving}
                        style={{ 
                          background: adminKBSaving ? '#f3f4f6' : '#10b981', 
                          color: adminKBSaving ? '#b6bbc6' : '#fff', 
                          border: 'none', 
                          borderRadius: 8, 
                          padding: '12px 24px', 
                          fontWeight: 600, 
                          fontSize: '1rem', 
                          cursor: adminKBSaving ? 'not-allowed' : 'pointer' 
                        }}
                      >
                        {adminKBSaving ? 'Saving...' : 'Save KB Entry'}
                      </button>
                    </div>
                  </div>
                </form>
                ) : (
                  /* File Upload Interface */
                  <form onSubmit={handleAdminKBFileUpload} style={{ marginBottom: 24, padding: 20, background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' }}>
                    <h3 style={{ marginBottom: 16, fontSize: '1.1rem', fontWeight: 600 }}>Upload Documents</h3>
                    
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Select Files * (Multiple files allowed)</label>
                      <input
                        type="file"
                        multiple
                        accept=".csv,.txt,.pdf,.doc,.docx,.xlsx,.xls"
                        onChange={handleAdminKBFileChange}
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                      />
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        Supported formats: CSV, TXT, PDF, DOC, DOCX, XLSX, XLS • You can select multiple files at once
                      </div>
                    </div>

                    {/* Selected Files */}
                    {adminKBUploadFiles.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <label style={{ fontWeight: 500, margin: 0 }}>Selected Files ({adminKBUploadFiles.length}):</label>
                          <button
                            type="button"
                            onClick={() => document.querySelector('input[type="file"]').click()}
                            style={{ 
                              background: '#10b981', 
                              color: '#fff', 
                              border: 'none', 
                              borderRadius: 4, 
                              padding: '4px 8px', 
                              fontSize: 12, 
                              cursor: 'pointer' 
                            }}
                          >
                            + Add More Files
                          </button>
                        </div>
                        <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                          {adminKBUploadFiles.map((file, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', background: '#fff', borderRadius: 4, marginBottom: 4 }}>
                              <span style={{ fontSize: 14 }}>{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeAdminKBFile(idx)}
                                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: 12, cursor: 'pointer' }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <button 
                        type="submit" 
                        disabled={adminKBUploading || adminKBUploadFiles.length === 0}
                        style={{ 
                          background: (adminKBUploading || adminKBUploadFiles.length === 0) ? '#f3f4f6' : '#10b981', 
                          color: (adminKBUploading || adminKBUploadFiles.length === 0) ? '#b6bbc6' : '#fff', 
                          border: 'none', 
                          borderRadius: 8, 
                          padding: '12px 24px', 
                          fontWeight: 600, 
                          fontSize: '1rem', 
                          cursor: (adminKBUploading || adminKBUploadFiles.length === 0) ? 'not-allowed' : 'pointer' 
                        }}
                      >
                        {adminKBUploading ? 'Uploading...' : `Upload ${adminKBUploadFiles.length} File(s)`}
                      </button>
                    </div>
                  </form>
                )}

                {/* Messages */}
                {adminKBError && <div style={{ color: 'red', marginBottom: 8, padding: 8, background: '#fef2f2', borderRadius: 6 }}>{adminKBError}</div>}
                {adminKBSuccess && <div style={{ color: 'green', marginBottom: 8, padding: 8, background: '#f0fdf4', borderRadius: 6 }}>{adminKBSuccess}</div>}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal for Edit Admin KB Entry */}
      {editAdminKBModal && selectedAdminKBEntry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '800px',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Edit Knowledge Base Entry</h2>
              <button 
                onClick={() => {
                  setEditAdminKBModal(false);
                  setSelectedAdminKBEntry(null);
                  setEditingAdminKBForm({ topic: '', content: '', tags: '', source_url: '', agent_id: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            {/* Entry Information Display */}
            <div style={{ marginBottom: 20, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>Entry Information</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Agent:</div>
                  <div style={{ fontWeight: 500, color: '#111' }}>{selectedAdminKBEntry.agent_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Last Updated:</div>
                  <div style={{ fontWeight: 500, color: '#111' }}>{new Date(selectedAdminKBEntry.updated_at).toLocaleString()}</div>
                </div>
              </div>
            </div>

            <form onSubmit={handleUpdateAdminKBEntry} style={{ marginBottom: 24, padding: 20, background: '#f9fafb', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <h3 style={{ marginBottom: 16, fontSize: '1.1rem', fontWeight: 600 }}>Update KB Entry</h3>
              
              <div style={{ display: 'grid', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Agent *</label>
                  <select
                    value={editingAdminKBForm.agent_id}
                    onChange={e => setEditingAdminKBForm({...editingAdminKBForm, agent_id: e.target.value})}
                    style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                    required
                  >
                    <option value="">Select an agent...</option>
                    {userAgents.map(agent => (
                      <option key={agent.agent_id} value={agent.agent_id}>
                        {agent.agent_name || agent.agent_id}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Topic *</label>
                  <input
                    type="text"
                    value={editingAdminKBForm.topic}
                    onChange={e => setEditingAdminKBForm({...editingAdminKBForm, topic: e.target.value})}
                    placeholder="Enter topic (3-120 characters)"
                    style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                    required
                  />
                </div>
                
                {/* Content section hidden in edit modal */}
                {false && (
                  <div>
                    <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Content *</label>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                      Content Length: {editingAdminKBForm.content.length} characters
                    </div>
                    <textarea
                      value={editingAdminKBForm.content}
                      onChange={e => setEditingAdminKBForm({...editingAdminKBForm, content: e.target.value})}
                      placeholder="Enter content (Markdown or plain text)"
                      rows={8}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none', resize: 'vertical' }}
                      required
                    />
                  </div>
                )}
                
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Tags</label>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                    {editingAdminKBForm.tags ? 
                      `Tags: ${editingAdminKBForm.tags.split(",").map(t => t.trim()).filter(Boolean).length}/10` : 
                      'No tags set'
                    }
                  </div>
                  <input
                    type="text"
                    value={editingAdminKBForm.tags}
                    onChange={e => setEditingAdminKBForm({...editingAdminKBForm, tags: e.target.value})}
                    placeholder="pricing, faq, support (comma-separated, max 10 tags)"
                    style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                  />
                  {editingAdminKBForm.tags && editingAdminKBForm.tags.split(",").map(t => t.trim()).filter(Boolean).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Current Tags:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {editingAdminKBForm.tags.split(",").map(t => t.trim()).filter(Boolean).map((tag, idx) => (
                          <span key={idx} style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: 4, fontSize: '0.8em' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Source URL (optional)</label>
                  <input
                    type="url"
                    value={editingAdminKBForm.source_url}
                    onChange={e => setEditingAdminKBForm({...editingAdminKBForm, source_url: e.target.value})}
                    placeholder="https://example.com, s3://bucket/file, /path/to/file, or any valid URL"
                    style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                  />
                </div>
                
                <div>
                  <button 
                    type="submit" 
                    disabled={updatingAdminKB}
                    style={{ 
                      background: updatingAdminKB ? '#f3f4f6' : '#10b981', 
                      color: updatingAdminKB ? '#b6bbc6' : '#fff', 
                      border: 'none', 
                      borderRadius: 8, 
                      padding: '12px 24px', 
                      fontWeight: 600, 
                      fontSize: '1rem', 
                      cursor: updatingAdminKB ? 'not-allowed' : 'pointer' 
                    }}
                  >
                    {updatingAdminKB ? 'Updating...' : 'Update KB Entry'}
                  </button>
                </div>
              </div>
            </form>

            {/* Messages */}
            {adminKBError && <div style={{ color: 'red', marginBottom: 8, padding: 8, background: '#fef2f2', borderRadius: 6 }}>{adminKBError}</div>}
            {adminKBSuccess && <div style={{ color: 'green', marginBottom: 8, padding: 8, background: '#f0fdf4', borderRadius: 6 }}>{adminKBSuccess}</div>}
          </div>
        </div>
      )}

      {/* Modal for Delete Admin KB Entry Confirmation */}
      {deleteAdminKBModal && selectedAdminKBEntry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: '90vw',
            width: '500px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Delete Knowledge Base Entry</h2>
              <button 
                onClick={() => {
                  setDeleteAdminKBModal(false);
                  setSelectedAdminKBEntry(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: 24, color: '#6b7280' }}>
              <p>Are you sure you want to delete this knowledge base entry?</p>
              <div style={{ background: '#f9fafb', padding: 12, borderRadius: 8, marginTop: 12 }}>
                <p style={{ margin: 0, fontWeight: 500 }}><strong>Topic:</strong> {selectedAdminKBEntry.topic}</p>
                <p style={{ margin: '4px 0 0 0' }}><strong>Agent:</strong> {selectedAdminKBEntry.agent_name}</p>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button 
                onClick={() => {
                  setDeleteAdminKBModal(false);
                  setSelectedAdminKBEntry(null);
                }}
                style={{ 
                  background: '#fff', 
                  color: '#222', 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 8, 
                  padding: '10px 24px', 
                  fontWeight: 500, 
                  fontSize: '1rem', 
                  cursor: 'pointer' 
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleConfirmDeleteAdminKBEntry}
                disabled={deletingAdminKB}
                style={{ 
                  background: deletingAdminKB ? '#f3f4f6' : '#dc2626', 
                  color: deletingAdminKB ? '#b6bbc6' : '#fff', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '10px 24px', 
                  fontWeight: 600, 
                  fontSize: '1rem', 
                  cursor: deletingAdminKB ? 'not-allowed' : 'pointer' 
                }}
              >
                {deletingAdminKB ? 'Deleting...' : 'Delete'}
              </button>
            </div>

            {/* Messages */}
            {adminKBError && <div style={{ color: 'red', marginTop: 12, padding: 8, background: '#fef2f2', borderRadius: 6 }}>{adminKBError}</div>}
            {adminKBSuccess && <div style={{ color: 'green', marginTop: 12, padding: 8, background: '#f0fdf4', borderRadius: 6 }}>{adminKBSuccess}</div>}
          </div>
        </div>
      )}
      
      {/* Modal for Add Knowledge Base */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(0,0,0,0.13)', width: 700, maxWidth: '95vw', padding: '36px 32px 28px 32px', position: 'relative' }}>
            {/* Close (X) icon */}
            <button onClick={() => setShowModal(false)} style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 24, color: '#6b7280', cursor: 'pointer' }}>
              <i className="fas fa-times"></i>
            </button>
            <h2 style={{ fontWeight: 600, fontSize: '1.35rem', marginBottom: 24 }}>Add Knowledge Base</h2>
            <form onSubmit={handleCreateKB}>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Knowledge Base Name</label>
                <input
                  type="text"
                  placeholder="Enter"
                  value={kbName}
                  onChange={e => setKbName(e.target.value)}
                  style={{ width: '100%', padding: '14px 18px', borderRadius: 12, border: '1px solid #e5e7eb', fontSize: '1.1rem', outline: 'none', marginBottom: 0, background: '#fafbfc' }}
                />
              </div>
              <div style={{ marginBottom: 28 }}>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Documents</label>
                <label style={{ display: 'block' }}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.csv,.txt,.xlsx,.xls,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <button type="button" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 28px', fontWeight: 500, fontSize: '1.1rem', color: '#222', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }} onClick={e => e.target.previousSibling.click()}>
                    <span style={{ fontSize: 20, fontWeight: 600, color: '#64748b' }}>+</span> Add
                  </button>
                </label>
                {/* Show selected files */}
                <div style={{ marginTop: 12 }}>
                  {files.map((file, idx) => (
                    <div key={idx} style={{ fontSize: '1rem', color: '#374151', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {file.name}
                      <button type="button" style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 16, cursor: 'pointer' }} onClick={() => removeFile(idx)} title="Remove file">
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ background: '#fff', color: '#222', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 28px', fontWeight: 500, fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={uploading} style={{ background: uploading ? '#f3f4f6' : '#2563eb', color: uploading ? '#b6bbc6' : '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontWeight: 600, fontSize: '1rem', cursor: uploading ? 'not-allowed' : 'pointer' }}>{uploading ? 'Saving...' : 'Save'}</button>
              </div>
              {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
            </form>
          </div>
        </div>
      )}

      {/* Modal for View KB Details */}
      {viewModal && selectedKB && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(0,0,0,0.13)', width: 800, maxWidth: '95vw', padding: '36px 32px 28px 32px', position: 'relative' }}>
            <button onClick={() => { setViewModal(false); setSelectedKBDetails(null); }} style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 24, color: '#6b7280', cursor: 'pointer' }}>
              <i className="fas fa-times"></i>
            </button>
            <h2 style={{ fontWeight: 600, fontSize: '1.2rem', marginBottom: 24 }}>Knowledge Base Details</h2>
            {loadingKBDetails ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, color: '#6b7280' }}></i>
                <div style={{ marginTop: 12, color: '#6b7280' }}>Loading details...</div>
              </div>
            ) : selectedKBDetails ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ marginBottom: 20 }}>
                    <h3 style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: 8 }}>{selectedKBDetails.knowledge_base.name}</h3>
                    <div style={{ color: '#6b7280', fontSize: 14 }}>
                      <div><strong>Retell KB ID:</strong> {selectedKBDetails.knowledge_base.retell_kb_id}</div>
                      <div><strong>Created:</strong> {new Date(selectedKBDetails.knowledge_base.created_at).toLocaleString()}</div>
                      <div><strong>Attached to {selectedKBDetails.attached_count} agent(s)</strong></div>
                      {selectedKBDetails.retell_details && (
                        <div><strong>Status:</strong> {selectedKBDetails.retell_details.status}</div>
                      )}
                    </div>
                  </div>
                  {selectedKBDetails.attached_agents.length > 0 && (
                    <div>
                      <h4 style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 12 }}>Attached Agents:</h4>
                      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {selectedKBDetails.attached_agents.map((agent, idx) => (
                          <div key={idx} style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: 6, marginBottom: 6, fontSize: 14 }}>
                            <div style={{ fontWeight: 500 }}>{agent.chatbot_name || 'Unnamed Agent'}</div>
                            <div style={{ color: '#6b7280', fontSize: 12, fontFamily: 'monospace' }}>{agent.agent_id}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h4 style={{ fontWeight: 600, fontSize: '1rem' }}>Files ({selectedKBDetails.files_count})</h4>
                    <button
                      onClick={() => {
                        setViewModal(false);
                        handleAddFiles(selectedKBDetails.knowledge_base);
                      }}
                      style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
                    >
                      <i className="fas fa-plus"></i> Add Files
                    </button>
                  </div>
                  
                  {selectedKBDetails.files && selectedKBDetails.files.length > 0 ? (
                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                      {selectedKBDetails.files.map(file => (
                        <div key={file.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f9fafb', borderRadius: 6, marginBottom: 6 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, fontSize: 14, color: '#111' }}>{file.filename}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                              {new Date(file.uploaded_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              onClick={() => handleDownloadFile(file)}
                              style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}
                              title="Download"
                            >
                              <i className="fas fa-download"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteFile(file)}
                              style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', background: '#f9fafb', borderRadius: 6 }}>
                      No files uploaded
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ color: 'red', textAlign: 'center' }}>Failed to load knowledge base details</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => { setViewModal(false); setSelectedKBDetails(null); }} style={{ background: '#6b7280', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 500, fontSize: '1rem', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Edit KB Name */}
      {editModal && selectedKB && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(0,0,0,0.13)', width: 500, maxWidth: '95vw', padding: '36px 32px 28px 32px', position: 'relative' }}>
            <button onClick={() => { setEditModal(false); setSelectedKB(null); setEditingName(''); }} style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 24, color: '#6b7280', cursor: 'pointer' }}>
              <i className="fas fa-times"></i>
            </button>
            <h2 style={{ fontWeight: 600, fontSize: '1.2rem', marginBottom: 24 }}>Edit Knowledge Base Name</h2>
            <form onSubmit={handleUpdateKBName}>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Knowledge Base Name</label>
                <input
                  type="text"
                  value={editingName}
                  onChange={e => setEditingName(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => { setEditModal(false); setSelectedKB(null); setEditingName(''); }} style={{ background: '#fff', color: '#222', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 24px', fontWeight: 500, fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={updatingName} style={{ background: updatingName ? '#f3f4f6' : '#10b981', color: updatingName ? '#b6bbc6' : '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, fontSize: '1rem', cursor: updatingName ? 'not-allowed' : 'pointer' }}>{updatingName ? 'Updating...' : 'Update'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for Delete KB Confirmation */}
      {deleteKBModal && selectedKB && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(0,0,0,0.13)', width: 500, maxWidth: '95vw', padding: '36px 32px 28px 32px', position: 'relative' }}>
            <button onClick={() => { setDeleteKBModal(false); setSelectedKB(null); }} style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 24, color: '#6b7280', cursor: 'pointer' }}>
              <i className="fas fa-times"></i>
            </button>
            <h2 style={{ fontWeight: 600, fontSize: '1.2rem', marginBottom: 16 }}>Delete Knowledge Base</h2>
            <div style={{ marginBottom: 24, color: '#6b7280' }}>
              <p>Are you sure you want to delete <strong>"{selectedKB.name}"</strong>?</p>
              <p style={{ fontSize: 14, marginTop: 8 }}>This action will:</p>
              <ul style={{ fontSize: 14, marginTop: 4, paddingLeft: 20 }}>
                <li>Remove the knowledge base from all attached agents</li>
                <li>Delete the knowledge base from Retell</li>
                <li>Permanently delete all associated data</li>
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => { setDeleteKBModal(false); setSelectedKB(null); }} style={{ background: '#fff', color: '#222', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 24px', fontWeight: 500, fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleConfirmDeleteKB} disabled={deletingKB} style={{ background: deletingKB ? '#f3f4f6' : '#dc2626', color: deletingKB ? '#b6bbc6' : '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, fontSize: '1rem', cursor: deletingKB ? 'not-allowed' : 'pointer' }}>{deletingKB ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Detach KB from Agent */}
      {detachModal && selectedKB && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(0,0,0,0.13)', width: 500, maxWidth: '95vw', padding: '36px 32px 28px 32px', position: 'relative' }}>
            <button onClick={() => { setDetachModal(false); setSelectedKB(null); setSelectedAgentToDetach(''); }} style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 24, color: '#6b7280', cursor: 'pointer' }}>
              <i className="fas fa-times"></i>
            </button>
            <h2 style={{ fontWeight: 600, fontSize: '1.2rem', marginBottom: 16 }}>Detach Knowledge Base</h2>
            <div style={{ marginBottom: 24 }}>
              <p style={{ marginBottom: 16 }}>Select an agent to detach <strong>"{selectedKB.name}"</strong> from:</p>
              <select
                value={selectedAgentToDetach}
                onChange={e => setSelectedAgentToDetach(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none' }}
              >
                <option value="">Select an agent...</option>
                {userAgents.filter(agent => {
                  const kbList = agentKBMap[agent.agent_id] || [];
                  return kbList.includes(selectedKB.id);
                }).map(agent => (
                  <option key={agent.agent_id} value={agent.agent_id}>
                                         {agent.chatbot_name || agent.agent_id} ({agent.agent_id.substring(0, 8)}...)
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => { setDetachModal(false); setSelectedKB(null); setSelectedAgentToDetach(''); }} style={{ background: '#fff', color: '#222', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 24px', fontWeight: 500, fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleConfirmDetachKB} disabled={detachingKB || !selectedAgentToDetach} style={{ background: (detachingKB || !selectedAgentToDetach) ? '#f3f4f6' : '#f59e0b', color: (detachingKB || !selectedAgentToDetach) ? '#b6bbc6' : '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, fontSize: '1rem', cursor: (detachingKB || !selectedAgentToDetach) ? 'not-allowed' : 'pointer' }}>{detachingKB ? 'Detaching...' : 'Detach'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Add Files to KB */}
      {addFilesModal && selectedKB && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(0,0,0,0.13)', width: 600, maxWidth: '95vw', padding: '36px 32px 28px 32px', position: 'relative' }}>
            <button onClick={() => { setAddFilesModal(false); setSelectedKB(null); setNewFiles([]); }} style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 24, color: '#6b7280', cursor: 'pointer' }}>
              <i className="fas fa-times"></i>
            </button>
            <h2 style={{ fontWeight: 600, fontSize: '1.2rem', marginBottom: 24 }}>Add Files to "{selectedKB.name}"</h2>
            <form onSubmit={handleConfirmAddFiles}>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Select Files</label>
                <label style={{ display: 'block' }}>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.csv,.txt,.xlsx,.xls,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileSelection}
                  />
                  <button type="button" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 28px', fontWeight: 500, fontSize: '1.1rem', color: '#222', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }} onClick={e => e.target.previousSibling.click()}>
                    <span style={{ fontSize: 20, fontWeight: 600, color: '#64748b' }}>+</span> Select Files
                  </button>
                </label>
                {/* Show selected files */}
                <div style={{ marginTop: 12 }}>
                  {newFiles.map((file, idx) => (
                    <div key={idx} style={{ fontSize: '1rem', color: '#374151', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {file.name}
                      <button type="button" style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 16, cursor: 'pointer' }} onClick={() => removeNewFile(idx)} title="Remove file">
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => { setAddFilesModal(false); setSelectedKB(null); setNewFiles([]); }} style={{ background: '#fff', color: '#222', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 28px', fontWeight: 500, fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={addingFiles || newFiles.length === 0} style={{ background: (addingFiles || newFiles.length === 0) ? '#f3f4f6' : '#10b981', color: (addingFiles || newFiles.length === 0) ? '#b6bbc6' : '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontWeight: 600, fontSize: '1rem', cursor: (addingFiles || newFiles.length === 0) ? 'not-allowed' : 'pointer' }}>{addingFiles ? 'Adding...' : 'Add Files'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for Delete File Confirmation */}
      {deleteFileModal && selectedFileToDelete && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(0,0,0,0.13)', width: 500, maxWidth: '95vw', padding: '36px 32px 28px 32px', position: 'relative' }}>
            <button onClick={() => { setDeleteFileModal(false); setSelectedFileToDelete(null); }} style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 24, color: '#6b7280', cursor: 'pointer' }}>
              <i className="fas fa-times"></i>
            </button>
            <h2 style={{ fontWeight: 600, fontSize: '1.2rem', marginBottom: 16 }}>Delete File</h2>
            <div style={{ marginBottom: 24, color: '#6b7280' }}>
              <p>Are you sure you want to delete <strong>"{selectedFileToDelete.filename}"</strong>?</p>
              <p style={{ fontSize: 14, marginTop: 8 }}>This action will:</p>
              <ul style={{ fontSize: 14, marginTop: 4, paddingLeft: 20 }}>
                <li>Remove the file from the knowledge base</li>
                <li>Delete the file from Retell</li>
                <li>Remove the file from storage</li>
              </ul>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button onClick={() => { setDeleteFileModal(false); setSelectedFileToDelete(null); }} style={{ background: '#fff', color: '#222', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 24px', fontWeight: 500, fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleConfirmDeleteFile} disabled={deletingFile} style={{ background: deletingFile ? '#f3f4f6' : '#dc2626', color: deletingFile ? '#b6bbc6' : '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, fontSize: '1rem', cursor: deletingFile ? 'not-allowed' : 'pointer' }}>{deletingFile ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal for Attach KB to Agent */}
      {attachModal && selectedKB && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(0,0,0,0.13)', width: 600, maxWidth: '95vw', padding: '36px 32px 28px 32px', position: 'relative' }}>
            <button onClick={() => setAttachModal(false)} style={{ position: 'absolute', top: 18, right: 18, background: 'none', border: 'none', fontSize: 24, color: '#6b7280', cursor: 'pointer' }}>
              <i className="fas fa-times"></i>
            </button>
            <h2 style={{ fontWeight: 600, fontSize: '1.2rem', marginBottom: 18 }}>Attach "{selectedKB.name}" to Agent</h2>
            <form onSubmit={handleAttachKB}>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Select Agent</label>
                {loadingAgents || loadingAgentKBMap ? (
                  <div style={{ padding: '12px 16px', color: '#6b7280' }}>Loading your agents...</div>
                ) : null}
                {!loadingAgents && !loadingAgentKBMap && (
                  (() => {
                    // Filter out agents that already have this KB attached
                    const availableAgents = userAgents.filter(agent => {
                      const kbList = agentKBMap[agent.agent_id] || [];
                      return !kbList.includes(selectedKB.id);
                    });
                    if (availableAgents.length === 0) {
                      return (
                        <div style={{ padding: '12px 16px', color: '#6b7280', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                          All your agents already have this knowledge base attached.
                        </div>
                      );
                    }
                    return (
                      <select
                        value={selectedAgentId}
                        onChange={e => setSelectedAgentId(e.target.value)}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '1rem', outline: 'none', background: '#fafbfc' }}
                      >
                        <option value="">Select an agent...</option>
                        {availableAgents.map(agent => (
                          <option key={agent.agent_id} value={agent.agent_id}>
                            {agent.chatbot_name || agent.agent_id} ({agent.agent_id.substring(0, 8)}...)
                          </option>
                        ))}
                      </select>
                    );
                  })()
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => setAttachModal(false)} style={{ background: '#fff', color: '#222', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 28px', fontWeight: 500, fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
                <button
                  type="submit"
                  disabled={
                    loadingAgents || loadingAgentKBMap ||
                    userAgents.filter(agent => {
                      const kbList = agentKBMap[agent.agent_id] || [];
                      return !kbList.includes(selectedKB.id);
                    }).length === 0 ||
                    !selectedAgentId
                  }
                  style={{
                    background: (loadingAgents || loadingAgentKBMap || userAgents.filter(agent => {
                      const kbList = agentKBMap[agent.agent_id] || [];
                      return !kbList.includes(selectedKB.id);
                    }).length === 0 || !selectedAgentId) ? '#f3f4f6' : '#2563eb',
                    color: (loadingAgents || loadingAgentKBMap || userAgents.filter(agent => {
                      const kbList = agentKBMap[agent.agent_id] || [];
                      return !kbList.includes(selectedKB.id);
                    }).length === 0 || !selectedAgentId) ? '#b6bbc6' : '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 28px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: (loadingAgents || loadingAgentKBMap || userAgents.filter(agent => {
                      const kbList = agentKBMap[agent.agent_id] || [];
                      return !kbList.includes(selectedKB.id);
                    }).length === 0 || !selectedAgentId) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Attach
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase; 